import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import HomePage from './components/home/HomePage';
import PrelaunchPage from './components/home/PrelaunchPage';
import SharedPosterView from './components/home/SharedPosterView';
import Survey from './components/Survey';
import LoginPopup from './components/auth/LoginPopup';
import SignUpPopup from './components/auth/SignUpPopup';
import SetPasswordPage from './components/auth/SetPasswordPage';
import LegalPage from './components/legal/LegalPage';
import PeopleAreFinishedPage from './components/manifesto/PeopleAreFinishedPage';
import { db, auth } from './firebase';
import { doc, getDoc, collection, query, where, limit, getDocs } from 'firebase/firestore';
import { capturePosterReferralFromSearch } from './utils/posterReferralLink';
import { parseWeeklyFeaturedSlotFromPath, resolveWeeklyFeaturedSlotPosterId } from './utils/weeklyFeaturedSlots';
import { onAuthStateChanged, signInWithCustomToken } from 'firebase/auth';
import { completeOAuthRedirectIfNeeded } from './utils/socialAuth';
import { provisionUserAfterAuth } from './utils/provisionUserAfterAuth';
import { captureDailyVibeInviteFromSearchParams } from './utils/dailyVibeEmailInvite';
import { tryFulfillDailyVibeEmailInvitesAfterAuth } from './utils/fulfillDailyVibeEmailInvitesAfterAuth';

// Flip to false when ready to launch the full site
const PRELAUNCH_MODE = false;

/** SetPasswordPage sets this before `location.href = '/'` so post-login Fox Chat runs after full reload (/set-password skips the first-snapshot open). */
const POST_LOGIN_COLLAGES_SESSION_KEY = 'luvwtf_postLoginCollages';

/** When /poster/:id matches a live feed doc, overlay these from wantedFeed — feed is updated on every save; sharedPosters/{feedAdId} is not. */
const WANTED_FEED_POSTER_OVERLAY_KEYS = [
  'partnerCutout',
  'heartPhotoUrl',
  'userPhotoUrl',
  'swags',
  'places',
  'activityX',
  'activityY',
  'activityZ',
  'posterNickname',
  'dominantPersonality',
  'alsoPrivateMatch',
  'friendLuv',
  'romanticLuv',
  'selfGender',
  'friendCollageShare',
  'friendFoxMaskPlaceholder',
  'userId',
  'userName',
  'contentFingerprint',
];

function App() {
  /** Client-side pathname so we can navigate between routes (e.g. /f -> /) without a full reload. */
  const [pathname, setPathname] = useState(
    typeof window !== 'undefined' ? window.location.pathname : '/'
  );
  const [showSurvey, setShowSurvey] = useState(false);
  const [showLoginPopup, setShowLoginPopup] = useState(false);
  const [showSignUpPopup, setShowSignUpPopup] = useState(false);
  const [signUpHint, setSignUpHint] = useState(null);
  const [signUpSource, setSignUpSource] = useState('navbar'); // 'navbar' | 'referral' | 'feed' | 'daily_vibe_invite'
  const [loginPopupInitialEmail, setLoginPopupInitialEmail] = useState('');
  const [loginPopupHint, setLoginPopupHint] = useState('');
  const [sharedMission, setSharedMission] = useState(null);
  const [loadingMission, setLoadingMission] = useState(false);
  const [sharedPoster, setSharedPoster] = useState(null);
  const [loadingSharedPoster, setLoadingSharedPoster] = useState(() =>
    typeof window !== 'undefined' &&
    (/^\/poster\/[a-zA-Z0-9_-]+$/.test(window.location.pathname) ||
      parseWeeklyFeaturedSlotFromPath(window.location.pathname) !== null)
  );
  const [currentUser, setCurrentUser] = useState(null);
  const [showHomeBehindSetPassword, setShowHomeBehindSetPassword] = useState(false);
  const [deepLinkChatId, setDeepLinkChatId] = useState(null);
  /** Email / deeplink `/?openMessages=1` — HomePage opens the Messages inbox (no specific thread). */
  const [deepLinkOpenMessagesTab, setDeepLinkOpenMessagesTab] = useState(false);
  /** Increment to open HomePage "Your collages" inline (e.g. after saving a friend-shared poster). */
  const [openMyCollagesNonce, setOpenMyCollagesNonce] = useState(0);
  /** Increment after successful sign-in so HomePage opens Fox Chat (vibe check or profile creation). */
  const [postLoginCollagesNonce, setPostLoginCollagesNonce] = useState(0);
  /** After `chatToken` sign-in targeting Your rewards (`/?chatToken=&yourRewards=1`), open collages + scroll #user-menu-your-rewards. */
  const [postLoginRewardsNonce, setPostLoginRewardsNonce] = useState(0);
  const sharedMissionDetailsRef = useRef(null);
  const authFirstSnapshotRef = useRef(true);
  const prevFirebaseUserRef = useRef(undefined);
  /** FoxChat registers `{ buildPosterForResetEmail, onPosterAttachedViaResetEmail }` for LoginPopup password-reset collage attach. */
  const foxChatResetEmailAttachHooksRef = useRef(null);

  // Keep client-side pathname in sync with browser navigation (back/forward).
  useEffect(() => {
    const onPopState = () => setPathname(window.location.pathname);
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  // SPA navigation to the homepage (no full reload) — keeps React state/auth alive.
  const navigateHome = () => {
    if (window.location.pathname !== '/') {
      window.history.pushState({}, '', '/');
    }
    setPathname('/');
    // Unlike a full reload, an in-place route swap keeps the old scroll offset, so reset to top.
    window.scrollTo(0, 0);
  };

  // Persist daily-vibe email invite inviter uid from deeplink until the friend signs in.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    captureDailyVibeInviteFromSearchParams(new URLSearchParams(window.location.search));
  }, []);

  // Re-open sign up after returning from /legal?from=signup (Back to sign up)
  useEffect(() => {
    if (window.location.pathname !== '/') return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('openSignup') !== '1') return;
    const src = params.get('signupSource');
    if (src === 'navbar' || src === 'referral' || src === 'feed' || src === 'daily_vibe_invite') {
      setSignUpSource(src);
    }
    captureDailyVibeInviteFromSearchParams(params);
    setShowSignUpPopup(true);
    params.delete('openSignup');
    params.delete('signupSource');
    const clean = params.toString();
    window.history.replaceState({}, '', window.location.pathname + (clean ? `?${clean}` : ''));
  }, []);

  // Handle inbox / email `chatToken` sign-in (`signInWithCustomToken` via Admin minted JWT)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const chatToken = params.get('chatToken');
    const openChat = params.get('openChat');
    /** HomePage skips the plain `yourRewards` effect when chatToken is present; we scroll after auth here. */
    const postRewardsLanding = params.get('yourRewards') === '1';

    if (params.get('openMessages') === '1' && !params.get('openChat')) {
      setDeepLinkOpenMessagesTab(true);
    }

    if (openChat) setDeepLinkChatId(openChat);

    if (chatToken) {
      signInWithCustomToken(auth, chatToken)
        .then(() => {
          params.delete('chatToken');
          if (postRewardsLanding) {
            params.delete('yourRewards');
            setPostLoginRewardsNonce((n) => n + 1);
          }
          const clean = params.toString();
          const newUrl = window.location.pathname + (clean ? `?${clean}` : '');
          window.history.replaceState({}, '', newUrl);
        })
        .catch((err) => {
          console.warn('Magic link sign-in failed (token may have expired):', err.message);
        });
    }
  }, []);

  // After full reload from SetPasswordPage, open Fox Chat (set-password itself skips the first-snapshot open)
  useEffect(() => {
    try {
      if (sessionStorage.getItem(POST_LOGIN_COLLAGES_SESSION_KEY) === '1') {
        sessionStorage.removeItem(POST_LOGIN_COLLAGES_SESSION_KEY);
        setPostLoginCollagesNonce((n) => n + 1);
      }
    } catch (_) {}
  }, []);

  // Finalize an OAuth signInWithRedirect (popup is primary; this resolves the fallback on return).
  useEffect(() => {
    (async () => {
      const result = await completeOAuthRedirectIfNeeded(auth);
      if (result?.user) {
        try {
          await provisionUserAfterAuth(result);
        } catch (err) {
          console.error('OAuth redirect provisioning failed:', err);
        }
      }
    })();
  }, []);

  // Listen for authentication state changes (anonymous users are used only for App Check + HTTPS functions; UI treats them as signed out).
  // Signed-in users open Fox Chat on HomePage — including a persisted session on refresh.
  // /set-password is excluded (HomePage is mounted behind the form; sessionStorage opens Fox Chat after redirect).
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      const prev = prevFirebaseUserRef.current;
      const onSetPasswordRoute =
        typeof window !== 'undefined' && window.location.pathname === '/set-password';
      const nowSignedIn = Boolean(user && !user.isAnonymous);

      const applyDisplayedUser = () => {
        if (user?.isAnonymous) {
          console.log('Auth: anonymous session (API access only)');
          setCurrentUser(null);
        } else if (user) {
          console.log('Auth state changed:', `User logged in: ${user.email || user.uid}`);
          setCurrentUser(user);
        } else {
          console.log('Auth state changed: No user');
          setCurrentUser(null);
        }
      };

      if (authFirstSnapshotRef.current) {
        authFirstSnapshotRef.current = false;
        prevFirebaseUserRef.current = user;
        applyDisplayedUser();
        if (nowSignedIn) {
          void tryFulfillDailyVibeEmailInvitesAfterAuth();
          if (!onSetPasswordRoute) {
            setPostLoginCollagesNonce((n) => n + 1);
          }
        }
        return;
      }

      const wasEffectivelyOut = !prev || prev.isAnonymous;
      if (nowSignedIn && wasEffectivelyOut && !onSetPasswordRoute) {
        setPostLoginCollagesNonce((n) => n + 1);
      }

      prevFirebaseUserRef.current = user;
      applyDisplayedUser();

      if (user && !user.isAnonymous) {
        void tryFulfillDailyVibeEmailInvitesAfterAuth();
      }
    });

    return () => unsubscribe();
  }, []);

  // Check for shared mission in URL
  useEffect(() => {
    const checkForSharedMission = async () => {
      const path = window.location.pathname;
      const match = path.match(/^\/mission\/([a-zA-Z0-9]+)$/);
      
      if (match && match[1]) {
        const missionId = match[1];
        setLoadingMission(true);
        
        try {
          const missionDoc = await getDoc(doc(db, 'missions', missionId));
          
          if (missionDoc.exists()) {
            // Get mission data and handle potential legacy data format
            const missionData = missionDoc.data();
            
            // Handle legacy missions that might not have all the fields
            // by providing fallback values
            const processedMission = {
              id: missionDoc.id,
              ...missionData,
              
              // Ensure location details exists with expected fields
              locationDetails: missionData.locationDetails || {
                name: missionData.location || "Mission Location",
                description: missionData.locationDesc || "Your LUV mission location",
                formattedAddress: "San Francisco, CA",
                coordinates: { lat: 37.7749, lng: -122.4194 },
                notes: "Outdoor venue, free admission. Limited parking available. The north end is clothing-optional. Accessible by MUNI 29 bus. Strong currents make swimming unsafe, but perfect for romantic shoreline walks. Bring a blanket and picnic."
              },
              
              // Ensure protocol and challenge fields exist
              missionProtocol: missionData.missionProtocol || missionData.qualifier || "Complete your mission qualifier",
              missionChallenge: missionData.missionChallenge || "Designed uniquely for each couple by LUV and revealed in person."
            };
            
            setSharedMission(processedMission);
          } else {
            console.error('Mission not found');
          }
        } catch (error) {
          console.error('Error loading shared mission:', error);
        } finally {
          setLoadingMission(false);
        }
      }
    };
    
    checkForSharedMission();
  }, []);

  // Check for shared wanted poster in URL (/poster/:id, or short weekly-featured slot link /1../5)
  useEffect(() => {
    const path = window.location.pathname;
    const match = path.match(/^\/poster\/([a-zA-Z0-9_-]+)$/);
    const weeklyFeaturedSlot = match ? null : parseWeeklyFeaturedSlotFromPath(path);
    if ((!match || !match[1]) && weeklyFeaturedSlot === null) return;
    let cancelled = false;
    setLoadingSharedPoster(true);
    (async () => {
      if (match && match[1]) return match[1];
      return resolveWeeklyFeaturedSlotPosterId(db, weeklyFeaturedSlot);
    })()
      .then(async (posterId) => {
        if (cancelled || !posterId) return;
        capturePosterReferralFromSearch(posterId);
        const snap = await getDoc(doc(db, 'sharedPosters', posterId));
        if (cancelled) return;
        if (!snap.exists()) return;
        const data = { id: snap.id, ...snap.data() };
        try {
          const feedSnap = await getDoc(doc(db, 'wantedFeed', posterId));
          if (!cancelled && feedSnap.exists()) {
            const fd = feedSnap.data();
            for (const k of WANTED_FEED_POSTER_OVERLAY_KEYS) {
              if (Object.prototype.hasOwnProperty.call(fd, k)) data[k] = fd[k];
            }
          }
        } catch (_) {}
        try {
          const optQ = query(
            collection(db, 'dinnerGiveawayOptIns'),
            where('posterId', '==', posterId),
            limit(25),
          );
          const optSnap = await getDocs(optQ);
          if (!cancelled) {
            for (const d of optSnap.docs) {
              const o = d.data();
              if (o.postedInFeed !== true) continue;
              const ba = o.bountyAmount;
              if (typeof ba === 'number' && Number.isFinite(ba)) {
                data.bountyAmount = ba;
                data.feedSource = 'dinnerGiveaway';
                break;
              }
            }
          }
        } catch (_) {}
        // Vibe aura for the nickname tile: prefer sharedPosters.auraColor; else userPublicPrimary (auth).
        if (!cancelled && !(data.auraColor && typeof data.auraColor === 'object') && data.userId) {
          try {
            const pubSnap = await getDoc(doc(db, 'userPublicPrimary', data.userId));
            const aura = pubSnap.exists() ? pubSnap.data()?.ad?.auraColor : null;
            if (aura && typeof aura === 'object') data.auraColor = aura;
          } catch (_) {}
        }
        if (!cancelled) setSharedPoster(data);
      })
      .catch((err) => { if (!cancelled) console.error('Error loading shared poster:', err); })
      .finally(() => { if (!cancelled) setLoadingSharedPoster(false); });
    return () => { cancelled = true; };
  }, []);

  /* /poster/:id aura backfill: the load above runs on mount, usually before Firebase auth
     restores, so the signed-in `userPublicPrimary` aura read fails silently. Retry once
     auth is ready so the nickname tile can render the owner's vibe aura. */
  useEffect(() => {
    if (!currentUser || !sharedPoster?.userId) return;
    if (sharedPoster.auraColor && typeof sharedPoster.auraColor === 'object') return;
    let cancelled = false;
    const posterId = sharedPoster.id;
    getDoc(doc(db, 'userPublicPrimary', sharedPoster.userId))
      .then((snap) => {
        if (cancelled || !snap.exists()) return;
        const aura = snap.data()?.ad?.auraColor;
        if (aura && typeof aura === 'object') {
          setSharedPoster((prev) => (prev && prev.id === posterId ? { ...prev, auraColor: aura } : prev));
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [currentUser, sharedPoster]);

  // On /set-password: show SetPasswordPage first, then load HomePage in background for blur
  useEffect(() => {
    const isSetPassword = window.location.pathname === '/set-password';
    if (!isSetPassword) {
      setShowHomeBehindSetPassword(false);
      return;
    }
    const id = requestAnimationFrame(() => {
      setShowHomeBehindSetPassword(true);
    });
    return () => cancelAnimationFrame(id);
  }, []);

  const handleShowSurvey = () => {
    setShowSurvey(true);
  };

  const handleShowLoginPopup = () => {
    setShowLoginPopup(true);
  };

  const handleCloseLoginPopup = () => {
    setShowLoginPopup(false);
    setLoginPopupInitialEmail('');
    setLoginPopupHint('');
  };

  const handleShowLoginPopupFromSignUp = (prefillEmail) => {
    setShowSignUpPopup(false);
    setSignUpHint(null);
    if (prefillEmail) {
      setLoginPopupInitialEmail(prefillEmail);
      setLoginPopupHint('This email is already registered. Please sign in instead.');
    } else {
      setLoginPopupInitialEmail('');
      setLoginPopupHint('');
    }
    setShowLoginPopup(true);
  };

  const handleSurveyClose = () => {
    try {
      localStorage.removeItem('luvMissionFromRecommendation');
      localStorage.removeItem('luvMissionCurrentQuestion');
      localStorage.removeItem('luvMissionAnswers');
      localStorage.removeItem('luvMissionInput');
    } catch (_) {}
    setShowSurvey(false);
  };

  const closeSharedPoster = () => {
    if (window.location.pathname !== '/') {
      window.history.replaceState({}, '', '/');
    }
    setPathname('/');
    setSharedPoster(null);
  };

  // Handle the case when a mission is shared
  if (loadingMission) {
    return (
      <div className="shared-mission-overlay">
        <div className="shared-mission-container">
          <div className="loading-indicator">Loading mission...</div>
        </div>
      </div>
    );
  }

  // Show SetPasswordPage immediately on /set-password; HomePage loads in background after first paint
  if (window.location.pathname === '/set-password' && !showHomeBehindSetPassword) {
    return <SetPasswordPage />;
  }

  if (window.location.pathname === '/legal') {
    return (
      <div className="App">
        <LegalPage />
      </div>
    );
  }

  if (!loadingMission && sharedMission) {
    return (
      <div className="shared-mission-overlay" style={{
        backgroundColor: '#fae6ff',
        backdropFilter: 'blur(10px)'
      }}>
        <div className="shared-mission-container" style={{
          boxShadow: '0 0 30px rgba(147, 51, 234, 0.6), 0 0 80px rgba(147, 51, 234, 0.4), 0 0 120px rgba(147, 51, 234, 0.2)',
          backgroundColor: 'white',
          borderRadius: '32px',
          position: 'relative',
          zIndex: '2',
          maxWidth: '520px',
          width: '100%',
          overflow: 'hidden'
        }}>
          <button 
            className="shared-mission-close" 
            onClick={() => {
              // Clear the mission from URL and state
              window.history.pushState({}, '', '/');
              setSharedMission(null);
            }}
          >
            &times;
          </button>
          
          <div 
            className="shared-mission-details"
            ref={sharedMissionDetailsRef}
            style={{
              padding: '2rem',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}
          >
            <div className="shared-mission-header">
              <div className="shared-mission-title">Your LUV Mission</div>
              <div className="shared-mission-name">{sharedMission.title}</div>
            </div>
            
            <div className="shared-mission-info">
              <div className="shared-mission-section">
                <div className="shared-mission-section-title">Mission Location</div>
                <div className="shared-mission-section-content">
                  {sharedMission.locationDetails.name}
                </div>
                <div className="shared-mission-section-description">
                  {sharedMission.locationDetails.description}
                </div>
                <div className="shared-mission-section-address">
                  {sharedMission.locationDetails.formattedAddress}
                </div>
              </div>
              
              <div className="shared-mission-section">
                <div className="shared-mission-section-title">Mission Protocol</div>
                <div className="shared-mission-section-content">
                  {sharedMission.missionProtocol}
                </div>
              </div>
              
              <div className="shared-mission-section">
                <div className="shared-mission-section-title">Mission Challenge</div>
                <div className="shared-mission-section-content">
                  {sharedMission.missionChallenge}
                </div>
              </div>
              
              <div className="shared-mission-section mission-notes">
                <div className="shared-mission-section-title">Notes</div>
                <div className="shared-mission-section-content">
                  {sharedMission.locationDetails.notes || "No additional notes for this mission."}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // While loading shared poster: show only loading overlay (poster loads before homepage)
  if (loadingSharedPoster) {
    return (
      <div className="shared-mission-overlay shared-poster-loading-overlay">
        <div className="shared-mission-container">
          <div className="loading-indicator shared-poster-loading-indicator">Loading poster...</div>
        </div>
      </div>
    );
  }

  if (PRELAUNCH_MODE) {
    return (
      <div className="App">
        <PrelaunchPage
          onShowLoginPopup={handleShowLoginPopup}
          onShowSignUp={(hint, source) => {
            setSignUpHint(hint || null);
            setSignUpSource(source || 'navbar');
            setShowSignUpPopup(true);
          }}
          currentUser={currentUser}
        />
        {showLoginPopup && (
          <LoginPopup 
            onClose={handleCloseLoginPopup} 
            onShowSurvey={handleShowSurvey}
            onShowSignUp={() => {
              setShowLoginPopup(false);
              setLoginPopupInitialEmail('');
              setLoginPopupHint('');
              setSignUpSource('navbar');
              setShowSignUpPopup(true);
            }}
            initialEmail={loginPopupInitialEmail}
            hintMessage={loginPopupHint}
            foxChatResetEmailAttachHooksRef={foxChatResetEmailAttachHooksRef}
          />
        )}
        {showSignUpPopup && (
          <SignUpPopup 
            onClose={() => {
              setShowSignUpPopup(false);
              setSignUpHint(null);
              setSignUpSource('navbar');
            }} 
            onShowLoginPopup={handleShowLoginPopupFromSignUp}
            hint={signUpHint}
            signupSource={signUpSource}
            isPrelaunch
            foxChatResetEmailAttachHooksRef={foxChatResetEmailAttachHooksRef}
          />
        )}
      </div>
    );
  }

  return (
    <div className="App">
      <HomePage 
        onShowSurvey={handleShowSurvey}
        onShowLoginPopup={handleShowLoginPopup}
        onShowSignUp={(hint, source) => {
          setSignUpHint(hint || null);
          setSignUpSource(source || 'navbar');
          setShowSignUpPopup(true);
        }}
        currentUser={currentUser}
        signUpPopupOpen={showSignUpPopup}
        loginPopupOpen={showLoginPopup}
        deepLinkChatId={deepLinkChatId}
        onDeepLinkChatHandled={() => setDeepLinkChatId(null)}
        deepLinkOpenMessagesTab={deepLinkOpenMessagesTab}
        onDeepLinkOpenMessagesTabHandled={() => setDeepLinkOpenMessagesTab(false)}
        openMyCollagesNonce={openMyCollagesNonce}
        postLoginCollagesNonce={postLoginCollagesNonce}
        postLoginRewardsNonce={postLoginRewardsNonce}
        foxChatResetEmailAttachHooksRef={foxChatResetEmailAttachHooksRef}
        onLogoClick={sharedPoster ? closeSharedPoster : undefined}
      />

      {(pathname === '/f' || pathname === '/f/') && (
        <div className="paf-route-overlay">
          <PeopleAreFinishedPage
            currentUser={currentUser}
            onNavigateHome={navigateHome}
            onShowLoginPopup={handleShowLoginPopup}
            onShowSignUp={(hint, source) => {
              setSignUpHint(hint || null);
              setSignUpSource(source || 'navbar');
              setShowSignUpPopup(true);
            }}
          />
        </div>
      )}

      {sharedPoster && (
        <SharedPosterView
          sharedPoster={sharedPoster}
          onClose={closeSharedPoster}
          currentUser={currentUser}
          onShowSignUp={(hint) => {
            setSignUpHint(hint || null);
            setSignUpSource('referral');
            setShowSignUpPopup(true);
          }}
          onOpenChat={(conversationId) => {
            closeSharedPoster();
            setDeepLinkChatId(conversationId);
          }}
          onFriendSharedSaveSuccess={() => {
            closeSharedPoster();
            setOpenMyCollagesNonce((n) => n + 1);
          }}
        />
      )}

      {showSurvey && (
        <Survey 
          onClose={handleSurveyClose}
          className="higher-survey"
        />
      )}
      
      {showLoginPopup && (
        <LoginPopup 
          onClose={handleCloseLoginPopup} 
          onShowSurvey={handleShowSurvey}
          onShowSignUp={() => {
            setShowLoginPopup(false);
            setLoginPopupInitialEmail('');
            setLoginPopupHint('');
            setSignUpSource('navbar');
            setShowSignUpPopup(true);
          }}
          initialEmail={loginPopupInitialEmail}
          hintMessage={loginPopupHint}
          foxChatResetEmailAttachHooksRef={foxChatResetEmailAttachHooksRef}
        />
      )}
      {showSignUpPopup && (
        <SignUpPopup 
          onClose={() => {
            setShowSignUpPopup(false);
            setSignUpHint(null);
            setSignUpSource('navbar');
          }} 
          onShowLoginPopup={handleShowLoginPopupFromSignUp}
          hint={signUpHint}
          signupSource={signUpSource}
          foxChatResetEmailAttachHooksRef={foxChatResetEmailAttachHooksRef}
        />
      )}
      {window.location.pathname === '/set-password' && <SetPasswordPage />}
    </div>
  );
}

export default App;
